import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import React, { useCallback, useState } from "react"
import { Link } from 'react-router-dom'
import { useAppContext } from "../../contexts/AppContext"
import { colorGrey } from "../../rsc/colors/colors"
import './StatementPage.scss'

import { CircularProgress } from '@mui/material'
import {
    DataMillStatement, DatePickerSlotProps, MillStatementContent, MillStatementData
} from '@pempem-org/components'
import '@pempem-org/components/styles'
import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import { client } from '../..'
import { GetOrgPaymentMethods } from '../../api/orgs'
import { PaymentModel } from '../../models/organisation/PaymentModel'
import { TotalDuePaymentComp } from './TotalDuePaymentComp'
import { PayNowButton } from "./PayNowButton"


type StatementDate = {
    startDate: string,
    endDate: string
}

type CardNumberStatementProps = {
    label: string
    value: number
    classLabel?: string
    classValue?: string
}

const Pagination = 10

export const CardNumberStatement: React.FC<CardNumberStatementProps> = ({ label, value, classLabel, classValue }) => {
    return <div className="lightBgColor pempem-flex-col-end gap10" style={{ borderRadius: 4, padding: '0 10px' }}>
        <p className={`${classLabel}`} style={{ color: colorGrey() }}>{label}</p>
        <p className="font-12">Rp <span className={`${classValue}`}>{value?.IndoFormat()}</span></p>
    </div>
}

// Use the local calendar day: UTC shifts the defaults by a day (or a month) near midnight.
function getDefaultStart(): string {
    return dayjs().startOf('month').format('YYYY-MM-DD')
}
function getDefaultEnd(): string {
    return dayjs().format('YYYY-MM-DD')
}

const RouterLink: React.FC<{ to: string; children: React.ReactNode; style?: React.CSSProperties }> = ({
    to,
    children,
    style,
}) => (
    <Link to={to} style={style}>
        {children}
    </Link>
)

const DatePickerSlot: React.FC<DatePickerSlotProps> = ({ value, onChange, maxDate, minDate, label }) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
            label={label}
            value={value ? dayjs(value) : null}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            minDate={minDate ? dayjs(minDate) : undefined}
            onChange={(newValue: Dayjs | null) => {
                if (newValue) onChange(newValue.format('YYYY-MM-DD'))
            }}
            slotProps={{ textField: { size: 'small' } }}
        />
    </LocalizationProvider>
)

const maxEnddate = dayjs()
const HIDE_TOTAL_BAR = false
const HIDE_TOTAL_DUE = false

export const StatementPage: React.FC = () => {

    const { millSelected } = useAppContext()

    const [loading, setLoading] = React.useState(false)

    function convertDayjsDateToUtc(date: Dayjs): string {
        const rawDate = date.format('YYYY-MM-DD')
        const utcDate = dayjs.utc(rawDate).toISOString()
        return utcDate
    }

    const [startDate, setStartDate] = useState(getDefaultStart)
    const [endDate, setEndDate] = useState(getDefaultEnd)
    const [data, setData] = useState<MillStatementData | null>(null)
    const [payments, setPayments] = useState<PaymentModel | null>(null)

    const token = useSelector((state: any) => state.token.value)

    useQuery(GetOrgPaymentMethods, {
        skip: !millSelected,
        variables: {
            id: parseInt(millSelected?.id)
        },
        onCompleted: (d) => {
            setPayments(PaymentModel.build(d))
        }
    })

    const onResult = useCallback((result: MillStatementData) => {
        setData(result)
    }, [])

    // On reload the mill is only selected once the profile has loaded.
    if (!millSelected) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <CircularProgress />
        </div>
    }

    return <div>
        <DataMillStatement
            orgId={millSelected.id}
            startDate={startDate}
            endDate={endDate}
            onResult={onResult}
            setLoading={setLoading}
            client={client as any}
            token={token}
        />
        {loading && !data && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <CircularProgress />
            </div>
        )}
        {data && (
            <div style={{ background: 'var(--color-surface)' }}>
                {payments && (
                    <div style={{ padding: 20 }}>
                        <PayNowButton payments={payments} />
                    </div>
                )}
                <MillStatementContent
                    data={data}
                    orgId={millSelected.id}
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    DatePickerSlot={DatePickerSlot}
                    client={client as any}
                    token={token}
                    renderLink={RouterLink as any}
                    loadingIndicator={<div>Loading...</div>}
                />
            </div>
        )}
        {!loading && !data && (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                No data available
            </div>
        )}
    </div>
}

function instance(date?: string | number | Date | Dayjs): Dayjs {
    throw new Error('Function not implemented.')
}
